import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, X, Star, Loader2, Mail, Lock, ArrowRight } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { TwoFactorChallengeModal } from '../../components/auth/TwoFactorChallengeModal';
import loginHero from '../../assets/login-hero.jpeg';
import '../../components/layouts/staff.css';

export default function InstructorLogin() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showForceOption, setShowForceOption] = useState(false);
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [pendingAuthData, setPendingAuthData] = useState<{ user: any; token: string } | null>(null);
    const { login } = useAuth();
    const navigate = useNavigate();

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const handleLogin = async (e: React.FormEvent, isForce: boolean = false) => {
        if (e) e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ 
                    email, 
                    password, 
                    role: 'instructor',
                    force: isForce 
                })
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 423 && data.action_required === 'confirm_force_login') {
                    setShowForceOption(true);
                }
                throw new Error(data.message || 'Login failed. Please verify your instructor credentials.');
            }

            if (data.two_factor_required && !data.user?.two_factor_exempt) {
                setPendingAuthData({ user: data.user, token: data.token });
                setShow2FAModal(true);
            } else {
                login(data.user, data.token);
                navigate('/instructor-dashboard');
            }
        } catch (err: any) {
            console.error('Instructor Login Error:', err);
            setError(err.message || 'A network error occurred. Please check your connection.');
        } finally {
            setLoading(false);
        }
    };

    const handleVerify2FACode = async (code: string) => {
        try {
            const res = await fetch(`${API_URL}/2fa/verify-login`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json' 
                },
                body: JSON.stringify({ email, code })
            });
            const data = await res.json();
            if (res.ok && data.token) {
                const authenticatedUser = {
                    ...(pendingAuthData?.user || {}),
                    ...(data.user || {}),
                    two_factor_enabled: true,
                };
                login(authenticatedUser, data.token);
                setShow2FAModal(false);
                navigate('/instructor-dashboard');
            } else {
                throw new Error(data.message || 'Invalid verification code or recovery key');
            }
        } catch (err: any) {
            throw err;
        }
    };

    return (
        <div className="flex min-h-screen bg-white dark:bg-charcoal-900 overflow-hidden relative">
            {/* Left Panel - Instructor Form (Pure White Background, Inverted Placement) */}
            <div className="flex-1 flex flex-col justify-between p-8 sm:p-12 lg:p-16 xl:p-20 bg-white dark:bg-charcoal-900 relative min-h-screen overflow-y-auto">
                <div className="w-full max-w-[420px] mx-auto my-auto py-8 animate-fade-in-up">
                    {/* Logo shown only on mobile */}
                    <div className="flex justify-center mb-8 lg:hidden">
                        <div className="bg-white p-3.5 rounded-2xl shadow-md border border-slate-100 dark:border-white/5">
                            <img
                                src="/logo-v2.png"
                                alt="Layos Group LLC"
                                className="h-10 w-auto"
                            />
                        </div>
                    </div>

                    <div className="mb-8">
                        <span className="text-[11px] font-black uppercase tracking-wider text-cyan-600 dark:text-cyan-400">Instructor Portal</span>
                        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mt-1 tracking-tight">Instructor Sign In</h1>
                        <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm font-medium mt-1">Manage your courses, assignments, and student cohorts.</p>
                    </div>

                    {error && (
                        <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 rounded-2xl mb-6 text-sm font-bold shadow-sm animate-in slide-in-from-top-2">
                            <div className="flex items-center gap-3">
                                <AlertCircle size={18} />
                                <span className="flex-1">{error}</span>
                                <button
                                    onClick={() => { setError(null); setShowForceOption(false); }}
                                    className="bg-transparent border-none text-red-400 hover:text-red-600 dark:hover:text-red-300 cursor-pointer p-1 transition-colors"
                                >
                                    <X size={14} />
                                </button>
                            </div>

                            {showForceOption && (
                                <button
                                    onClick={() => handleLogin(null as any, true)}
                                    className="w-full mt-4 py-3 bg-red-600 text-white rounded-full font-black text-xs uppercase tracking-widest border-none cursor-pointer hover:bg-red-700 transition-colors"
                                >
                                    Sign out other devices & Enter
                                </button>
                            )}
                        </div>
                    )}

                    <form onSubmit={handleLogin} className="space-y-5">
                        <div className="space-y-1.5">
                            <label className="block text-xs font-extrabold text-slate-500 dark:text-slate-400 ml-1">Instructor Email</label>
                            <div className="relative">
                                <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <input
                                    type="email"
                                    className="w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-charcoal-800 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white outline-none text-sm font-bold focus:bg-white dark:focus:bg-charcoal-800 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                                    placeholder="instructor@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <div className="flex justify-between items-center px-1">
                                <label className="block text-xs font-extrabold text-slate-500 dark:text-slate-400">Password</label>
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1 bg-transparent border-none cursor-pointer transition-colors"
                                >
                                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            <div className="relative">
                                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    className="w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-charcoal-800 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white outline-none text-sm font-bold focus:bg-white dark:focus:bg-charcoal-800 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 transition-all"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="flex justify-end px-1">
                            <Link to="/forgot-password" title="Forgot password" className="text-xs font-bold text-slate-500 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors underline underline-offset-4">Forgot your password?</Link>
                        </div>

                        <button
                            type="submit"
                            className="group w-full py-4 bg-brand-charcoal hover:bg-black dark:bg-cyan-500 dark:text-brand-charcoal dark:hover:bg-cyan-400 text-white rounded-xl font-bold text-sm tracking-widest uppercase active:scale-[0.98] hover:scale-[1.01] transition-all disabled:opacity-70 disabled:pointer-events-none border-none cursor-pointer flex items-center justify-center gap-2 shadow-lg shadow-black/10"
                            disabled={loading}
                        >
                            {loading ? (
                                <div className="flex items-center gap-3 justify-center">
                                    <Loader2 className="animate-spin" size={18} />
                                    <span>Authenticating...</span>
                                </div>
                            ) : (
                                <>
                                    <span>Sign In as Instructor</span>
                                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>

                    <div className="mt-8 text-center">
                        <Link to="/login" className="text-xs font-bold text-slate-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors uppercase tracking-[0.1em] inline-flex items-center justify-center gap-1.5">
                            Are you a student? <span className="text-cyan-500 underline underline-offset-4 font-bold">Back to Student Login</span>
                        </Link>
                    </div>
                </div>

                <div className="w-full flex items-center justify-center pt-6 text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mt-auto border-t border-slate-100 dark:border-white/5">
                    <div>© 2026 LGL Consulting. All rights reserved.</div>
                </div>
            </div>

            {/* Right Panel - Hero Visual (Rounded Edge Image Layout on Right) */}
            <div className="hidden lg:flex lg:w-1/2 p-4 lg:p-5 xl:p-6 relative">
                <div
                    className="w-full h-full relative flex flex-col justify-between p-12 xl:p-16 text-white overflow-hidden bg-cover bg-center rounded-[28px] xl:rounded-[36px] shadow-2xl border border-black/5 dark:border-white/10"
                    style={{
                        backgroundImage: `url(${loginHero})`,
                    }}
                >
                    {/* Cyberpunk dark cyan grid overlay */}
                    <div className="absolute inset-0 bg-charcoal-950/75 pointer-events-none" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50 pointer-events-none" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(6,182,212,0.2),transparent_75%)] pointer-events-none" />

                    {/* Top Content: White Logo Container */}
                    <div className="relative z-10">
                        <div className="inline-flex items-center h-16 px-6 rounded-2xl bg-white shadow-xl shadow-black/25 border border-white/20">
                            <img
                                src="/logo-v2.png"
                                alt="Layos Group LLC"
                                className="h-9 w-auto object-contain"
                            />
                        </div>
                    </div>

                    {/* Center/Bottom Content */}
                    <div className="relative z-10 mt-auto space-y-6">
                        {/* Decorative Concept Bullet */}
                        <div className="w-5 h-5 rounded-full bg-cyan-400 shadow-lg shadow-cyan-400/40"></div>

                        <div className="space-y-3">
                            <h2 className="text-3xl xl:text-4xl font-extrabold tracking-tight leading-tight max-w-md text-white drop-shadow-sm">
                                Empower Your Students
                            </h2>
                            <p className="text-white/80 text-xs xl:text-sm font-medium leading-relaxed max-w-sm">
                                Access advanced teaching tools, student metrics, curriculum builders, and live cohorts designed for modern educators.
                            </p>
                        </div>

                        {/* Floating Premium Badge */}
                        <div className="bg-white/10 backdrop-blur-xl p-4 rounded-2xl border border-white/20 shadow-xl inline-block max-w-[280px]">
                            <div className="flex items-center gap-3.5">
                                <div className="w-9 h-9 bg-cyan-500/20 rounded-xl flex items-center justify-center text-lg shadow-inner">
                                    <Star className="text-cyan-400 fill-cyan-400" size={16} />
                                </div>
                                <div className="pr-2">
                                    <div className="font-extrabold text-[11px] uppercase tracking-wider text-white">Instructor Portal</div>
                                    <div className="text-[9px] font-bold text-white/60 uppercase tracking-widest mt-0.5">Empowering Educators</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <TwoFactorChallengeModal
                isOpen={show2FAModal}
                email={email}
                onClose={() => setShow2FAModal(false)}
                onVerify={handleVerify2FACode}
            />
        </div>
    );
}
